import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Logo } from "./Logo";
import { ViewAsBanner, ViewAsSelector } from "./ViewAs";
import { ROLE_LABELS } from "../lib/re";
import {
  LayoutDashboard, KanbanSquare, Search, Wallet, Contact, FolderOpen,
  Users, UserCircle, LogOut, Menu, X, ShieldCheck, Bell, Building2, KeyRound, UserCheck, FileCheck2, Settings,
} from "lucide-react";

const NAV = [
  { to: "/painel", label: "Painel de Desempenho", icon: LayoutDashboard, id: "dashboard" },
  { to: "/imoveis", label: "Imóveis", icon: Building2, id: "imoveis" },
  { to: "/proprietarios", label: "Proprietários", icon: KeyRound, id: "proprietarios" },
  { to: "/clientes", label: "Clientes", icon: UserCheck, id: "clientes" },
  { to: "/documentacao", label: "Documentação", icon: FileCheck2, id: "documentacao", hideFor: ["leitura", "suporte"] },
  { to: "/leads", label: "Gestão de Leads", icon: KanbanSquare, id: "leads" },
  { to: "/bolsa-procura", label: "Bolsa de Procura", icon: Search, id: "bolsa" },
  { to: "/financeiro", label: "Financeiro", icon: Wallet, id: "financeiro" },
  { to: "/cartao", label: "Cartão de Visita", icon: Contact, id: "cartao" },
  { to: "/documentos", label: "Documentos", icon: FolderOpen, id: "documentos" },
  { to: "/consultores", label: "Utilizadores & Acessos", icon: Users, id: "consultores", managerOnly: true },
  { to: "/configuracoes", label: "Configurações", icon: Settings, id: "configuracoes", adminOnly: true },
  { to: "/perfil", label: "O Meu Perfil", icon: UserCircle, id: "perfil" },
];

export function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { setOpen(false); }, [title]);

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotifs = () => api.get("/notifications").then((r) => { setNotifs(r.data.items); setUnread(r.data.unread); }).catch(() => {});
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const openNotifs = async () => {
    const willOpen = !notifOpen;
    setNotifOpen(willOpen);
    if (willOpen && unread > 0) { await api.post("/notifications/read-all"); setUnread(0); setTimeout(loadNotifs, 400); }
  };

  const items = NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.managerOnly || ["admin", "diretor"].includes(user?.role)) && !(n.hideFor || []).includes(user?.role));
  const roleLabel = ROLE_LABELS[user?.role] || (user?.role === "suporte" ? "Suporte" : user?.role);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Sidebar = (
    <aside className="flex h-full w-72 flex-col bg-[#0A1622] border-r border-[#1E3A56]">
      <div className="px-5 py-5 border-b border-[#1E3A56]">
        <Logo size="md" />
      </div>
      <div className="px-4 py-4 border-b border-[#1E3A56] flex items-center gap-3">
        <img src={user?.photo_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(user?.name || "A")}
             alt="" className="w-11 h-11 rounded-full object-cover ring-1 ring-[#C9A84C]/40" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-100 truncate">{user?.name}</div>
          <span className={`inline-flex items-center gap-1 mt-0.5 text-[10px] px-2 py-0.5 rounded-full border ${
            isAdmin ? "bg-[#C9A84C]/20 text-[#E0C574] border-[#C9A84C]/40" : "bg-slate-800 text-slate-300 border-slate-700"
          }`} data-testid="role-badge">
            <ShieldCheck size={11} /> {roleLabel}
          </span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-[#C9A84C]/15 text-[#E0C574] border border-[#C9A84C]/30 font-semibold"
                  : "text-slate-400 hover:text-slate-100 hover:bg-[#152A3E] border border-transparent"
              }`}>
            <n.icon size={18} /> <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#1E3A56]">
        <button onClick={doLogout} data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-all">
          <LogOut size={18} /> Terminar Sessão
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex ambient-glow bg-[#0D1B2A]">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">{Sidebar}</div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative z-50">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 lg:ml-72 min-w-0">
        <ViewAsBanner />
        <header className="sticky top-0 z-20 backdrop-blur-md bg-[#0D1B2A]/90 border-b border-[#1E3A56] px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>
            <h1 className="font-display text-lg sm:text-2xl text-[#C9A84C] tracking-wide" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <ViewAsSelector />
            <div className="relative">
              <button onClick={openNotifs} data-testid="notif-bell" className="relative text-slate-300 hover:text-[#C9A84C] p-2 rounded-lg hover:bg-[#152A3E] transition-all">
                <Bell size={20} />
                {unread > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C9A84C] text-[#0D1B2A] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto card-almar shadow-2xl z-50" data-testid="notif-panel">
                  <div className="px-4 py-3 border-b border-[#1E3A56] flex items-center justify-between sticky top-0 bg-[#152A3E]">
                    <span className="text-sm font-semibold text-slate-100">Notificações</span>
                    <button onClick={() => setNotifOpen(false)}><X size={16} className="text-slate-400" /></button>
                  </div>
                  {notifs.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-6">Sem notificações</p>
                  ) : notifs.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-[#1E3A56]/50 ${!n.read ? "bg-[#C9A84C]/5" : ""}`}>
                      <div className="flex items-start gap-2">
                        <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.kind === "correspondencia" ? "bg-emerald-400" : "bg-[#C9A84C]"}`} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-200 leading-snug">{n.message}</p>
                          <span className="text-[10px] text-slate-500">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span className={`hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${
              isAdmin ? "bg-[#C9A84C]/20 text-[#E0C574] border-[#C9A84C]/40" : "bg-slate-800 text-slate-300 border-slate-700"
            }`}>
              <ShieldCheck size={13} /> {isAdmin ? "Acesso Total" : roleLabel}
            </span>
            <img src={user?.photo_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(user?.name || "A")}
                 alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#C9A84C]/40" />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
